/* src/components/GroupDetails.js - v2.13 - Added Leave Group Option */
import React, { useState, useEffect, useCallback } from 'react';
import Feed from './Feed';

function GroupDetails({ 
    groupSlug, 
    user, 
    posts, 
    onAddPost, 
    onDeletePost, 
    onLikePost, 
    onAddComment, 
    onDeleteComment, 
    onEditComment, 
    onEditPost, 
    onNavigateProfile,
    onBack,
    onTriggerLogin
}) {
  
  const [group, setGroup] = useState(null);
  const [activeTab, setActiveTab] = useState('feed'); 
  const [settingsTab, setSettingsTab] = useState('general'); 
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  const fetchGroup = useCallback(async () => {
      try {
          const res = await fetch(`/api/groups/${groupSlug}`);
          if (res.ok) {
              const data = await res.json();
              setGroup(data);
          }
      } catch (e) { console.error("Group load error", e); }
  }, [groupSlug]);

  useEffect(() => {
      if (groupSlug) fetchGroup();
  }, [groupSlug, fetchGroup]);

  const [editFields, setEditFields] = useState({
      name: '', description: '', banner_url: '', icon_url: '', rules: '', members: [] 
  });

  useEffect(() => {
      if (group) {
          setEditFields({
              name: group.name || '',
              description: group.description || '',
              banner_url: group.banner_url || '',
              icon_url: group.icon_url || '',
              rules: group.rules || "1: No Promotions or Spam:\nSelf-promotion (includes other groups), spam, and irrelevant links aren't allowed. Keep it related to the topic of the group. Message an Admin with any questions.\n\n2: Be Kind and Courteous:\nNo hating on others. We're all in this together to create a welcoming environment. Let's treat everyone with respect.\n\n3: Zero Tolerance:\nInstant ban. Enough said. Report any malicious activity to an Admin.\n\n4: Failure to Follow Rules:\nFailure to follow these simple rules (no excuse for not reading the rules) will result in a ban.",
              members: group.members || []
          });
      }
  }, [group]);

  const groupPosts = posts.filter(p => p.group_slug === groupSlug);
  const groupGallery = groupPosts.filter(p => p.image);
  
  // GUEST GUARD & MEMBERSHIP CHECK
  const isCreator = user && group?.created_by === user.name;
  const currentUserMember = user && group?.members?.find(m => m.username === user.name);
  let userRole = currentUserMember ? currentUserMember.role : 'visitor';
  if (isCreator) userRole = 'admin';
  const canManage = userRole === 'admin';
  const isMember = !!currentUserMember || isCreator; // Boolean for membership

  const handleJoinGroup = async () => {
      if (!user) { if(onTriggerLogin) onTriggerLogin(); return; }
      try {
          await fetch(`/api/groups/${group.slug}/join`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ username: user.name })
          });
          fetchGroup(); 
      } catch (e) { console.error("Join error", e); }
  };

  // --- NEW: LEAVE GROUP FUNCTION ---
  const handleLeaveGroup = async () => {
      if(!window.confirm("Are you sure you want to leave this group?")) return;
      try {
          const res = await fetch(`/api/groups/${group.slug}/leave`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ username: user.name })
          });
          if(res.ok) {
              alert("You have left the group.");
              fetchGroup(); // Refresh state to update UI
          } else {
              alert("Failed to leave group.");
          }
      } catch (e) { console.error("Leave error", e); }
  };

  const handleDeleteGroup = async () => {
      const confirmName = prompt(`To delete this group, type "${group.name}" below:`);
      if (confirmName !== group.name) return;
      try {
          const res = await fetch(`/api/groups/${group.id}`, { method: 'DELETE' });
          if (res.ok) { alert("Group deleted."); onBack(); } else { alert("Delete failed."); }
      } catch (e) { console.error("Delete error", e); }
  };

  const handleUpdateGroup = async (updatedData) => {
      setGroup(updatedData); 
      try {
          await fetch(`/api/groups/${group.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(updatedData)
          });
      } catch (e) { console.error(e); }
  };

  const handleSaveSettings = () => {
      handleUpdateGroup({ ...group, ...editFields });
      setIsSettingsOpen(false);
  };

  const handleMemberRoleChange = (targetUsername, newRole) => {
      const updatedMembers = editFields.members.map(m => 
          (m.username === targetUsername) ? { ...m, role: newRole } : m
      );
      setEditFields({ ...editFields, members: updatedMembers });
  };

  const handleKickMember = (targetUsername) => {
      if(!window.confirm("Remove this member?")) return;
      const updatedMembers = editFields.members.filter(m => m.username !== targetUsername);
      setEditFields({ ...editFields, members: updatedMembers });
  };

  const handleUpload = async (e, field) => {
      const file = e.target.files[0];
      if (!file) return;
      const formData = new FormData();
      formData.append('file', file);
      try {
          const res = await fetch('/api/upload', { method: 'POST', body: formData });
          const data = await res.json();
          const key = field === 'banner' ? 'banner_url' : 'icon_url';
          setEditFields(prev => ({ ...prev, [key]: data.url }));
      } catch (err) { console.error("Upload error:", err); }
  };

  if (!group) return <div style={{ padding: '50px', textAlign: 'center' }}>Loading Group Details...</div>;

  return (
    <div className="feed-container">
      
      <div className="card" style={{ padding: 0, overflow: 'hidden', position: 'relative' }}>
        <button onClick={onBack} style={{ position: 'absolute', top: '20px', left: '20px', zIndex: 10, background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', padding: '5px 15px', borderRadius: '20px', cursor: 'pointer' }}>← Back</button>
        
        <div style={{ height: '200px', background: group.banner_url ? `url(${group.banner_url}) center/cover` : 'linear-gradient(45deg, #1e90ff, #2ed573)' }}>
            {canManage && (
                <button onClick={() => setIsSettingsOpen(true)} style={{ position: 'absolute', top: '20px', right: '20px', background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '50%', width: '35px', height: '35px', cursor: 'pointer', fontSize: '18px' }}>⚙️</button>
            )}
        </div>
        
        <div style={{ padding: '0 30px 30px 30px', marginTop: '-50px', textAlign: 'center' }}>
            <div style={{ width: '100px', height: '100px', borderRadius: '15px', background: '#333', margin: '0 auto 15px', border: '4px solid #2b2b36', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {group.icon_url ? <img src={group.icon_url} alt="Group" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: '40px' }}>👥</span>}
            </div>
            
            <h2 style={{ fontSize: '28px', marginBottom: '10px', cursor: 'pointer' }} onClick={() => window.location.reload()}>{group.name}</h2>
            <p style={{ maxWidth: '600px', margin: '0 auto 20px', color: '#a0a0b0', lineHeight: '1.5' }}>{group.description}</p>
            
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '20px' }}>
                <span style={{ background: 'rgba(255,255,255,0.1)', padding: '5px 15px', borderRadius: '20px', fontSize: '12px' }}>{group.member_count} Members</span>
                {isMember ? (
                    <span style={{ background: canManage ? '#ff4757' : 'rgba(46, 213, 115, 0.2)', color: canManage ? 'white' : '#2ed573', padding: '5px 15px', borderRadius: '20px', fontSize: '12px', textTransform: 'uppercase', fontWeight: 'bold' }}>{userRole}</span>
                ) : (
                    <button onClick={handleJoinGroup} style={{ background: '#2ed573', border: 'none', color: 'white', padding: '5px 20px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold' }}>+ Join Group</button>
                )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: '20px' }}>
                {['feed', 'gallery', 'about'].map(tab => (
                    <button key={tab} onClick={() => setActiveTab(tab)} style={{ flex: 1, padding: '15px', background: 'transparent', border: 'none', color: activeTab === tab ? '#ff4757' : '#a0a0b0', borderBottom: activeTab === tab ? '2px solid #ff4757' : 'none', fontWeight: 'bold', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '1px' }}>{tab}</button>
                ))}
            </div>
        </div>
      </div>

      {activeTab === 'feed' && (
          isMember ? (
            <Feed 
                user={user} posts={groupPosts} 
                onAddPost={(post) => onAddPost({ ...post, group_slug: groupSlug })}
                onDeletePost={onDeletePost} onLikePost={onLikePost} onAddComment={onAddComment}
                onDeleteComment={onDeleteComment} onEditComment={onEditComment} onEditPost={onEditPost}
                onNavigateProfile={onNavigateProfile}
                onTriggerLogin={onTriggerLogin} 
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#888', background: '#1e1e24', marginTop: '10px', borderRadius: '15px' }}>
                <h3>🔒 Members Only</h3>
                <p>You must join this community to view and post content.</p>
                <button onClick={handleJoinGroup} style={{ background: '#2ed573', border: 'none', color: 'white', padding: '10px 25px', borderRadius: '20px', fontSize: '1rem', cursor: 'pointer', fontWeight: 'bold', marginTop: '10px' }}>Join Now</button>
            </div>
          )
      )}

      {activeTab === 'gallery' && (
          <div className="card" style={{ padding: '20px' }}>
              <h3 style={{ marginBottom: '20px' }}>Group Photos</h3>
              {groupGallery.length > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                      {groupGallery.map(p => (
                          <img key={p.id} src={p.image} alt="Gallery" style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer' }} onClick={() => setLightboxImage(p.image)} />
                      ))}
                  </div>
              ) : <div style={{ color: '#666', textAlign: 'center' }}>No photos yet.</div>}
          </div>
      )}

      {activeTab === 'about' && (
          <div className="card" style={{ padding: '30px' }}>
              <h3 style={{ color: '#ff4757' }}>Group Rules</h3>
              <p style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', marginBottom: '30px' }}>{group.rules || "1: No Promotions or Spam:\nSelf-promotion (includes other groups), spam, and irrelevant links aren't allowed. Keep it related to the topic of the group. Message an Admin with any questions.\n\n2: Be Kind and Courteous:\nNo hating on others. We're all in this together to create a welcoming environment. Let's treat everyone with respect.\n\n3: Zero Tolerance:\nInstant ban. Enough said. Report any malicious activity to an Admin.\n\n4: Failure to Follow Rules:\nFailure to follow these simple rules (no excuse for not reading the rules) will result in a ban."}</p>
              <h3 style={{ color: '#2ed573', marginBottom: '15px' }}>Leadership</h3>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '30px' }}>
                  {group.members?.filter(m => m.role === 'admin' || m.role === 'moderator').map(m => (
                      <div key={m.username} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '8px' }}>
                          <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: '#555', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{m.username[0].toUpperCase()}</div>
                          <div><div style={{ fontWeight: 'bold' }}>{m.username}</div><div style={{ fontSize: '10px', color: '#a0a0b0', textTransform: 'uppercase' }}>{m.role}</div></div>
                      </div>
                  ))}
              </div>
              <h3 style={{ color: '#1e90ff', marginBottom: '15px' }}>Community</h3>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {group.members?.filter(m => !m.role || m.role === 'member').map(m => (
                      <div key={m.username} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '8px' }}>
                          <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: '#555', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{m.username[0].toUpperCase()}</div>
                          <div><div style={{ fontWeight: 'bold' }}>{m.username}</div></div>
                      </div>
                  ))}
              </div>

              {/* --- NEW: LEAVE GROUP BUTTON --- */}
              {isMember && !isCreator && (
                  <div style={{ marginTop: '30px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '20px' }}>
                      <button onClick={handleLeaveGroup} style={{ background: 'transparent', border: '1px solid #ff4757', color: '#ff4757', padding: '10px 20px', borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold' }}>Leave Group</button>
                  </div>
              )}
          </div>
      )}

      {isSettingsOpen && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.9)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <div className="card" style={{ width: '90%', maxWidth: '600px', height: '80vh', display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
                  <div style={{ padding: '20px', borderBottom: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3 style={{ margin: 0 }}>Group Settings</h3>
                      <button onClick={() => setIsSettingsOpen(false)} style={{ background: 'none', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer' }}>✕</button>
                  </div>
                  <div style={{ display: 'flex', background: 'rgba(0,0,0,0.2)' }}>
                      {['general', 'rules', 'members'].map(t => (
                          <button key={t} onClick={() => setSettingsTab(t)} style={{ flex: 1, padding: '15px', background: settingsTab === t ? 'rgba(255,255,255,0.05)' : 'transparent', border: 'none', color: settingsTab === t ? '#ff4757' : '#aaa', cursor: 'pointer', textTransform: 'uppercase', fontWeight: 'bold' }}>{t}</button>
                      ))}
                  </div>
                  <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                      {settingsTab === 'general' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                              <label>Group Name <input className="post-input" value={editFields.name} onChange={(e) => setEditFields({...editFields, name: e.target.value})} /></label>
                              <label>Description <textarea className="post-input" style={{ minHeight: '80px' }} value={editFields.description} onChange={(e) => setEditFields({...editFields, description: e.target.value})} /></label>
                              <label>Upload Banner <input type="file" onChange={(e) => handleUpload(e, 'banner')} /></label>
                              <label>Upload Icon <input type="file" onChange={(e) => handleUpload(e, 'icon')} /></label>
                              <div style={{ marginTop: '30px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '20px' }}>
                                  <button onClick={handleDeleteGroup} style={{ width: '100%', background: '#ff4757', color: 'white', border: 'none', padding: '10px', borderRadius: '5px', fontWeight: 'bold', cursor: 'pointer' }}>⚠️ DELETE THIS GROUP</button>
                              </div>
                          </div>
                      )}
                      {settingsTab === 'rules' && (
                          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                              <label style={{ marginBottom: '10px' }}>Group Rules</label>
                              <textarea className="post-input" style={{ flex: 1, minHeight: '300px', resize: 'none' }} value={editFields.rules} onChange={(e) => setEditFields({...editFields, rules: e.target.value})} />
                          </div>
                      )}
                      {settingsTab === 'members' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              {editFields.members.map(member => (
                                  <div key={member.username} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><div style={{ width: '30px', height: '30px', background: '#555', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{member.username[0].toUpperCase()}</div><span>{member.username}</span></div>
                                      {user.name !== member.username && (<div style={{ display: 'flex', gap: '5px' }}><select value={member.role} onChange={(e) => handleMemberRoleChange(member.username, e.target.value)} style={{ background: '#222', color: 'white', border: '1px solid #444', padding: '5px', borderRadius: '4px' }}><option value="member">Member</option><option value="moderator">Moderator</option><option value="admin">Admin</option></select><button onClick={() => handleKickMember(member.username)} style={{ background: '#ff4757', color: 'white', border: 'none', borderRadius: '4px', padding: '5px 10px', cursor: 'pointer' }}>Kick</button></div>)}
                                  </div>
                              ))}
                          </div>
                      )}
                  </div>
                  <div style={{ padding: '20px', borderTop: '1px solid var(--glass-border)', textAlign: 'right' }}>
                      <button onClick={handleSaveSettings} style={{ padding: '10px 30px', background: '#2ed573', color: 'white', border: 'none', borderRadius: '30px', fontWeight: 'bold', cursor: 'pointer' }}>Save Changes</button>
                  </div>
              </div>
          </div>
      )}

      {lightboxImage && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.95)', zIndex: 3000, display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={() => setLightboxImage(null)}>
              <img src={lightboxImage} style={{ maxWidth: '90%', maxHeight: '90%', borderRadius: '10px' }} alt="Fullscreen" />
          </div>
      )}
    </div>
  );
}

export default GroupDetails;
